<template>
  <form @submit.prevent>
    <div>
      <h2>
        <span v-if="isEditMode">{{ $t("common.update") }} "{{ editedEntity.name | translate }}" {{ $t("common.update") }}</span>
        <span v-else>{{ $t("risk.add-asset-group") }}</span>
      </h2>

      <h6>{{ $t("asset-group-category.text") }}</h6>
      <filter-select
        id="assetGroupCategoryFilter"
        ref="assetGroupCategoryFilter"
        :placeholder="$t('asset-group-category.text')"
        :multiple="false"
        @filter="onAssetGroupCategorySearch"
        :selected="form.assetGroupCategory"
        @changed="onAssetGroupCategorySelect"
        :disabled="isEditMode"
      />

      <h6>{{ $t("asset-group.text") }}</h6>

      <filter-select
        id="assetGroupFilter"
        ref="assetGroupFilter"
        :placeholder="$t('asset-group.text')"
        :multiple="false"
        :displayCount="3"
        @filter="onAssetGroupSearch"
        :selected="form.assetGroup"
        @changed="assetGroupSelect"
        track="_id"
        :disabled="isEditMode"
      />

      <h6>{{ $t("asset-group.degree-of-criticality") }}</h6>
      <select class="form-control">
        <option v-if="form.assetGroup && form.assetGroup.criticalityLevel" :value="form.assetGroup.criticalityLevel">{{ form.assetGroup.criticalityLevel }}</option>
      </select>

      <div v-if="form.assetGroup && !form.assetGroup.criticalityLevel" class="text-danger mt-1">
        {{ $t("risk.missing-asset-group-ciriticality-warning") }}
      </div>

      <div>
        <h6>
          {{ $t("risk.possibility") }}
          <i id="popover-possibility" class="bx bx-question-mark rounded-circle ml-2 bg-primary text-white"></i>
        </h6>
        <b-popover container="popover-info" custom-class="wide-popover" target="popover-possibility" triggers="hover focus" placement="right">
          <!-- <template #title>İstatistikler</template> -->
          <table class="table mb-0">
            <thead>
              <tr>
                <th>{{ $t("risk.possibility") }}</th>
                <th>{{ $t("risk.info.possibility.title") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="level in [1, 2, 3, 4, 5]" :key="`possibility-${level}`">
                <td>
                  <p class="mb-1">{{ level }}-{{ $t(getAppliedRiskValueKey(level)) }}</p>
                </td>
                <td>
                  <p v-for="(line, idx) in getPossibilityLines(level)" :key="`possibility-${level}-${idx}`" class="mb-1">{{ line }}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </b-popover>

        <select class="form-control " v-model="form.possibility">
          <option :value="1">1-{{ $t("risk.applied-risk-value.lowest") }}</option>
          <option :value="2">2-{{ $t("risk.applied-risk-value.low") }}</option>
          <option :value="3">3-{{ $t("risk.applied-risk-value.medium") }}</option>
          <option :value="4">4-{{ $t("risk.applied-risk-value.high") }}</option>
          <option :value="5">5-{{ $t("risk.applied-risk-value.highest") }}</option>
        </select>
      </div>

      <div>
        <h6 style="display: inline-block;">
          {{ $t("risk.effect") }}
          <i id="popover-effect" class="bx bx-question-mark rounded-circle ml-2 bg-primary text-white"></i>
        </h6>

        <b-popover container="popover-info" custom-class="wide-popover" target="popover-effect" triggers="hover focus" placement="right">
          <!-- <template #title>İstatistikler</template> -->
          <table class="table mb-0">
            <thead>
              <tr>
                <th>{{ $t("risk.effect") }}</th>
                <th>{{ $t("risk.info.effect.title") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="level in [1, 2, 3, 4, 5]" :key="`effect-${level}`">
                <td>
                  <p class="mb-1">{{ level }}</p>
                </td>
                <td>
                  <p class="mb-1">{{ $t(`risk.info.effect.${level}`) }}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </b-popover>

        <select class="form-control" v-model="form.effectValue">
          <option :value="1">1</option>
          <option :value="2">2</option>
          <option :value="3">3</option>
          <option :value="4">4</option>
          <option :value="5">5</option>
        </select>
      </div>
      <div>
        <h6>{{ $t("common.description") }}</h6>
        <textarea v-model="form.description" type="text" class="form-control" :placeholder="$t('common.description')" rows="3"></textarea>
      </div>

      <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating || $v.$invalid" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-2">
        {{ $t("common.save") }}
      </sweet-button>
    </div>
  </form>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import ThreatForm from "./management-library-risk-threat-definition-form";
import WeaknessForm from "./management-library-risk-weakness-definition-form";
const state = "organizationRiskAssetGroupRelation";

export default {
  components: {
    ThreatForm,
    WeaknessForm,
  },
  props: ["risk"],
  data() {
    return {
      form: {
        assetGroupCategory: undefined,
        assetGroup: undefined,
        possibility: undefined,
        effectValue: undefined,
      },
      formError: undefined,
    };
  },
  validations: {
    form: {
      assetGroupCategory: { required },
      assetGroup: { required },
      possibility: { required },
      effectValue: { required },
    },
  },

  watch: {
    editedEntity: {
      deep: true,
      immediate: true,
      handler(v) {
        this.form = { ...this.prepareFormForEdit(v) };
      },
    },
  },
  computed: {
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    editedEntity() {
      return this.$store.getters[`${state}/editedEntity`];
    },
    isEditMode() {
      return this.$store.getters[`${state}/isEditMode`];
    },
  },
  mounted() {},
  methods: {
    getAppliedRiskValueKey(level) {
      const map = {
        1: "risk.applied-risk-value.lowest",
        2: "risk.applied-risk-value.low",
        3: "risk.applied-risk-value.medium",
        4: "risk.applied-risk-value.high",
        5: "risk.applied-risk-value.highest",
      };

      return map[level] || "risk.applied-risk-value.lowest";
    },
    getPossibilityLines(level) {
      const raw = this.$t(`risk.info.possibility.${level}`);

      if (!raw || typeof raw !== "string") {
        return [];
      }

      return raw
        .split(/\n+/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    },
    async onAssetGroupCategorySearch(value, callback) {
      let productQuery = undefined;
      if (this.currentProduct && this.currentProduct == "DDO") {
        productQuery = { value: this.currentProduct, isObject: true };
      }
      const response = await this.$store.dispatch(`organizationAssetGroupCategory/paginate`, {
        options: { limit: 1000, sort: { name: "asc" } },
        query: { name: { value }, products: productQuery },
      });

      callback(response.data?.docs ?? []);
    },
    onAssetGroupCategorySelect(assetGroupCategory) {
      this.form = { ...this.form, assetGroupCategory };
      this.$refs.assetGroupFilter.reset();
    },
    async onAssetGroupSearch(value, callback) {
      if (!this.form.assetGroupCategory) {
        return;
      }
      const response = await this.$store.dispatch(`organizationAssetGroup/paginate`, {
        options: { limit: 200 },
        query: { assetGroupCategory: { value: this.form.assetGroupCategory._id, isObject: true }, name: { value }, "scope.value": { value: "system", isObject: true } },
      });
      callback(response.data?.docs ?? []);
    },
    assetGroupSelect(item) {
      this.form = { ...this.form, assetGroup: item };
    },
    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }

      if (!this.risk.name.tr) {
        const risk = await this.$store.dispatch("organizationRisk/preparePayload", {
          editedEntity: this.$store.getters["organizationRisk/editedEntity"],
          form: this.risk,
        });
        this.risk = risk;
      }

      try {
        const payload = { ...this.form, risk: this.risk };
        if (!this.risk.products || this.products.length == 0) {
          payload.risk.products = [this.currentProduct];
        }
        const response = await this.$store.dispatch(`${state}/save`, payload);

        if (!this.risk._id) {
          this.$emit("relationWithRiskCreated", response.data);
        }
        this.form = {};
        this.$toast.success(this.$t("common.success"));
        this.$emit("saved");

        this.$root.$emit("bv::toggle::collapse", "sidebar-new-asset-group");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    threatRightPanelClosed() {
      this.$refs.threatFilter.reset();
    },
  },
};
</script>
